import { getGlobalHeadersAsObject } from "../global-headers";
import type { DRFListResponseWrapper, User } from "../types";
import { BoardColumnType, Status } from "./board-types";

const API_PATH = "/api/v2/personassignment2/";
const API_PATH_FULL_PA = "/api/v2/fullpersonassignment/";

interface PersonAssignmentBaseType {
	status_str?: string;
	column: string;
	snoozed?: string;
	archived?: boolean;
	created_by?: number;
	created_by_first_name?: string;
}

export interface PersonAssignmentCreateType extends PersonAssignmentBaseType {
	person: number;
	assignment: number;
	assignee?: number;
}

export interface PersonAssignmentRetrieveBase extends PersonAssignmentBaseType {
	id: number;
	created: string;
	/**
	 * Where Jobylon holds this person on this role.
	 *
	 * Null when Jobylon holds no application for them here, which covers every
	 * ordinary role and also a Jobylon role someone reached ODI's board of
	 * without being sent to Jobylon. An empty string means Jobylon does hold
	 * them but ODI has not read that job's pipeline, so the stage has no name
	 * yet.
	 */
	jobylon_stage?: string | null;
	/**
	 * Whether ODI may move this person between that job's Jobylon stages. False
	 * for anyone who applied through Jobylon's own form — the Push API only
	 * accepts updates to applications ODI created.
	 */
	jobylon_can_move?: boolean;
}

export interface PersonAssignmentFilled {
	person?: number;
	assignment?: number;
	assignee?: number;
	person_name?: string;
	assignment_name?: string;
}

export type PersonAssignmentRetrieveType = PersonAssignmentRetrieveBase &
	PersonAssignmentFilled;

/**
 * Fetches PersonAssignments with automatic pagination handling
 * @param assignmentId - Single assignment ID
 * @param assignmentIds - Multiple assignment IDs (batched query, more efficient)
 * @param personIds - Filter by person IDs
 * @param limit - Page size (default 100 for testing)
 * @returns All PersonAssignments across all pages
 */
export const getPersonAssignments = async ({
	personIds,
	assignmentId,
	assignmentIds,
	limit = 100,
}: {
	personIds?: number[];
	assignmentId?: number;
	assignmentIds?: number[];
	limit?: number;
}): Promise<PersonAssignmentRetrieveType[]> => {
	const allResults: PersonAssignmentRetrieveType[] = [];
	const params = new URLSearchParams();

	if (personIds && personIds.length > 0) {
		for (const id of personIds) {
			params.append("persons", id.toString());
		}
	}
	if (assignmentIds && assignmentIds.length > 0) {
		params.append("assignment__id__in", assignmentIds.join(","));
	} else if (assignmentId) {
		params.append("assignment__id", assignmentId.toString());
	}
	params.append("limit", limit.toString());

	let url: string | null = `${API_PATH}?${params}`;

	while (url) {
		const response = await fetch(url, {
			method: "GET",
			headers: getGlobalHeadersAsObject(),
		});
		const data: DRFListResponseWrapper<PersonAssignmentRetrieveType[]> =
			await response.json();

		allResults.push(...data.results);

		// Convert absolute next URL to relative (DRF returns internal Django port, not Vite proxy)
		if (data.next) {
			const nextUrl = new URL(data.next);
			url = nextUrl.pathname + nextUrl.search;
		} else {
			url = null;
		}

		console.debug(
			`[Pagination] Fetched ${data.results.length} results, total: ${allResults.length}/${data.count}, next: ${url ? "yes" : "done"}`
		);
	}

	return allResults;
};

/**
 *
 * @param assignmentId
 * @param personIds
 * @returns Full PersonAssignment tracking history for a given personId or assignmentId
 */
export const getAllPersonAssignments = ({
	personId,
	assignmentId,
}: {
	personId?: number;
	assignmentId?: number;
}): Promise<DRFListResponseWrapper<PersonAssignmentRetrieveType[]>> => {
	const params = new URLSearchParams();

	if (personId) {
		params.append("person", personId.toString());
	}
	if (assignmentId) {
		params.append("assignment", assignmentId.toString());
	}

	return fetch(`${API_PATH_FULL_PA}?` + params, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

//Do not expose outside of this file, use a wrapper, e.g. setPersonAssignmentStatus
export const createPersonAssignmentRecord = (
	data: PersonAssignmentCreateType
): Promise<PersonAssignmentRetrieveType> => {
	return fetch(API_PATH, {
		method: "POST",
		body: JSON.stringify(data),
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
	}).then((res) => res.json());
};

export const getPersonAssignmentByPersonAssignmentId = (
	id: number
): Promise<PersonAssignmentRetrieveType> => {
	return fetch(`${API_PATH}${id}/`, {
		method: "GET",
		headers: { ...getGlobalHeadersAsObject() },
	}).then((res) => res.json());
};

//TODO: update two fetching on creating status
export const setPersonAssignmentStatus = async ({
	newStatus,
	currentUser,
	column,
	itemToUpdate,
	newAssignee,
}: {
	newStatus: Status;
	currentUser: User;
	column: BoardColumnType;
	itemToUpdate: PersonAssignmentRetrieveType;
	newAssignee?: number | null;
}) => {
	const data: PersonAssignmentCreateType = {
		person: itemToUpdate.person,
		assignment: itemToUpdate.assignment,
		status_str: newStatus.name,
		column: column.name,
		created_by: currentUser.id,
		assignee: newAssignee !== undefined ? newAssignee : itemToUpdate.assignee,
	};

	const newPersonAssignment = await createPersonAssignmentRecord(data);
	return { oldPersonAssignment: itemToUpdate, newPersonAssignment };
};

export const setPersonAssignmentAssignee = async ({
	newAssignee,
	currentUser,
	itemToUpdate,
}: {
	newAssignee: number;
	currentUser: User;
	itemToUpdate: PersonAssignmentRetrieveType;
}) => {
	const data: PersonAssignmentCreateType = {
		person: itemToUpdate.person,
		assignment: itemToUpdate.assignment,
		status_str: itemToUpdate.status_str,
		column: itemToUpdate.column,
		created_by: currentUser.id,
		assignee: newAssignee,
	};

	const newPersonAssignment = await createPersonAssignmentRecord(data);
	return { oldPersonAssignment: itemToUpdate, newPersonAssignment };
};

/**
 * @param archived set to true to archive, false to unarchive
 * @param currentUser User object of current user
 * @param itemToUpdate PersonAssignment to archive
 * @returns {oldPersonAssignment, newPersonAssignment} where oldPersonAssignment is the PersonAssignment before archiving and newPersonAssignment is the PersonAssignment after archiving
 * Do not copy over archive field when creating a new person assignment
 */
export const archivePersonAssignment = async ({
	archived = true,
	currentUser,
	itemToUpdate,
}: {
	archived?: boolean;
	currentUser: User;
	itemToUpdate: PersonAssignmentRetrieveType;
}) => {
	const data: PersonAssignmentCreateType = {
		person: itemToUpdate.person,
		assignment: itemToUpdate.assignment,
		status_str: itemToUpdate.status_str,
		column: itemToUpdate.column,
		created_by: currentUser.id,
		assignee: itemToUpdate.assignee,
		archived: archived,
	};

	const newPersonAssignment = await createPersonAssignmentRecord(data);
	return { oldPersonAssignment: itemToUpdate, newPersonAssignment };
};

/**
 * @param snoozed timestamp of when to unsnooze
 * @param currentUser User object of current user
 * @param itemToUpdate PersonAssignment to archive
 * @returns {oldPersonAssignment, newPersonAssignment} where oldPersonAssignment is the PersonAssignment before archiving and newPersonAssignment is the PersonAssignment after archiving
 * Note That we do not want to copy over snooze time when updating a snoozed PersonAssignment (snooze items disappear from the board during the snooze-time, when they appear again they should be considered unsnoozed)
 */
export const snoozePersonAssignment = async ({
	snoozed,
	currentUser,
	itemToUpdate,
}: {
	snoozed: string;
	currentUser: User;
	itemToUpdate: PersonAssignmentRetrieveType;
}) => {
	const data: PersonAssignmentCreateType = {
		person: itemToUpdate.person,
		assignment: itemToUpdate.assignment,
		status_str: itemToUpdate.status_str,
		column: itemToUpdate.column,
		created_by: currentUser.id,
		assignee: itemToUpdate.assignee,
		snoozed: snoozed,
	};

	const newPersonAssignment = await createPersonAssignmentRecord(data);
	return { oldPersonAssignment: itemToUpdate, newPersonAssignment };
};

export const addPersonAssignment = async ({
	data,
}: {
	data: PersonAssignmentCreateType;
}) => {
	const newPersonAssignment = await createPersonAssignmentRecord(data);
	return { newPersonAssignment };
};

export const deletePersonAssignment = (personAssignmentId: number): Promise<any> => {
	if (!personAssignmentId || typeof personAssignmentId !== "number") {
		throw new Error(
			`deletePersonAssignment called with invalid argument id '${personAssignmentId}'. Expected number`
		);
	}

	return fetch(`/api/v2/personassignment2/${personAssignmentId}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	})
		.then((res) => {
			if (res.status !== 204) {
				throw new Error(
					`Something went wrong deleting personAssignment with id ${personAssignmentId}. Response statusText: ${res.statusText}`
				);
			}
			console.log(res);
		})
		.catch((err) => console.log(err));
};
